/**
 * `useAllocationGaps` — TanStack Query hooks for the #1812 fixed-placement
 * gap surface (`GET /api/cycles/{id}/allocation-gaps` + the one-click
 * cover placement `POST .../{fixed_shift_id}/cover`).
 *
 * Lead-only (the GET 403s for members). A gap is one approved fixed shift
 * (a standing weekly allocation's materialised pin, or a hand pin) whose
 * owner has a recorded absence that day — the seat the 8am reshuffle used
 * to manage from memory. The payload carries the seat, the absence, the
 * seat state and the candidate pool; per-candidate load figures are NOT on
 * this payload — consumers join against the `/roster` payload's
 * `members[].hours` / `workload_score` (one arithmetic, #1223).
 *
 * Consumed by `<AllocationGapsSection/>` (the pre-solve/coverage checklist
 * row list) and `<RosterHeatmap/>` (the per-cell open-seat callout) — both
 * read the same cache key so the grid badge and the checklist can't
 * disagree.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "../lib/AuthContext";
import { type ApiError, apiFetch } from "./client";
import { invalidateRosterReads } from "./rosterInvalidation";
import type { components } from "./schema.gen";

export type AllocationGapsResponse = components["schemas"]["AllocationGapsResponse"];
export type AllocationGap = components["schemas"]["AllocationGap"];
export type GapCandidate = components["schemas"]["GapCandidate"];
export type PlaceCoverResponse = components["schemas"]["PlaceCoverResponse"];

const STALE_TIME_MS = 30_000;

/** Prefix of every account's gap read for one schedule — what a finished
 *  solve and a publish invalidate: the key sits outside the `["cycle"]`
 *  root the post-solve catch-all covers, so a solve that seated the cover
 *  left "Fixed placements that can't happen" reading "Seat open" until a
 *  reload (truth pass r3-s4-198). */
export function allocationGapsRootKey(cycleId: string): readonly unknown[] {
  return ["cycle-allocation-gaps", cycleId];
}

export function allocationGapsQueryKey(
  cycleId: string,
  accountId: string | null,
): readonly unknown[] {
  return [...allocationGapsRootKey(cycleId), accountId ?? ""];
}

interface UseAllocationGapsOpts {
  /** Force-disable the fetch (members never mount this surface). */
  enabled?: boolean;
}

export function useAllocationGaps(cycleId: string, opts: UseAllocationGapsOpts = {}) {
  const { accountId } = useAuth();
  const enabledByCaller = opts.enabled ?? true;
  return useQuery<AllocationGapsResponse, ApiError | Error>({
    queryKey: allocationGapsQueryKey(cycleId, accountId),
    enabled: Boolean(cycleId && accountId) && enabledByCaller,
    staleTime: STALE_TIME_MS,
    retry: false,
    queryFn: () => apiFetch<AllocationGapsResponse>(`/api/cycles/${cycleId}/allocation-gaps`),
  });
}

/**
 * One-click cover placement. On success the seat's world changes in three
 * caches at once: the gap list (the row resolves away), the roster payload
 * (the cover pin appears on the grid), and the cycle dashboard (the
 * `pin_on_leave` pre-solve row clears) — invalidate all three.
 */
export function usePlaceGapCover(cycleId: string) {
  const { accountId } = useAuth();
  const qc = useQueryClient();
  return useMutation<
    PlaceCoverResponse,
    ApiError | Error,
    { fixedShiftId: string; membershipId: string }
  >({
    mutationFn: ({ fixedShiftId, membershipId }) =>
      apiFetch<PlaceCoverResponse>(`/api/cycles/${cycleId}/allocation-gaps/${fixedShiftId}/cover`, {
        method: "POST",
        body: JSON.stringify({ membership_id: membershipId }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: allocationGapsQueryKey(cycleId, accountId) });
      void invalidateRosterReads(qc, cycleId);
      qc.invalidateQueries({ queryKey: ["cycle-dashboard", cycleId] });
    },
  });
}
